import React, { useEffect, useState } from "react";
import { ymd } from "../lib/dates";
import { toast } from "sonner";
import { Money, CalendarBlank, IdentificationCard, Printer, ArrowSquareOut, User, ClockCounterClockwise } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import api, { formatIDR, formatDate, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { generatePDF } from "../lib/pdf";

export default function PortalKaryawan() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [slips, setSlips] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [rapor, setRapor] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [checkingIn, setCheckingIn] = useState(false);

  const load = async () => {
    try {
      const [p, s, l, r, a] = await Promise.all([
        api.get("/employees/me"),
        api.get("/slips/me"),
        api.get("/leaves"),
        api.get("/rapor"),
        api.get("/attendance"),
      ]);
      setProfile(p.data); setSlips(s.data); setLeaves(l.data); setRapor(r.data); setAttendance(a.data);
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const checkIn = async () => {
    setCheckingIn(true);
    try { await api.post("/attendance/check-in"); toast.success("Check-in berhasil"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCheckingIn(false); }
  };
  const checkOut = async () => {
    setCheckingIn(true);
    try { await api.post("/attendance/check-out"); toast.success("Check-out berhasil"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCheckingIn(false); }
  };

  const today = ymd();
  const todayAtt = attendance.find((a) => a.date === today);

  const printSlip = (s) => {
    generatePDF({
      title: `Slip Gaji ${s.period}`,
      subtitle: profile?.name || user?.name,
      headers: ["Komponen", "Nilai"],
      rows: [
        ["Gaji Pokok", formatIDR(s.base_salary)],
        ["Tunjangan", formatIDR(s.tunjangan)],
        ["Lembur", formatIDR(s.lembur)],
        ["Bonus", formatIDR(s.bonus)],
        ["Total Bruto", formatIDR(s.gross)],
        ["Potongan", formatIDR(s.potongan)],
        ["Pajak", formatIDR(s.pajak)],
        ["Take Home Pay (Net)", formatIDR(s.net)],
      ],
      meta: { "Karyawan": profile?.name || user?.name, "ID": profile?.employee_id || "-", "Jabatan": profile?.position || "-", "Periode": s.period },
      filename: `slip-${s.period}.pdf`,
    });
  };

  return (
    <div data-testid="portal-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Karyawan</div>
        <h1 className="font-display text-3xl font-black text-gray-900">Portal Saya</h1>
        <p className="text-gray-600 text-sm mt-1">Akses informasi personal, slip gaji, cuti, dan performa Anda.</p>
      </div>

      {/* Profile Card */}
      <div className="bg-gradient-to-r from-[#0B8D95] to-[#48B8BD] rounded-md p-6 text-white mb-6 flex flex-col md:flex-row md:items-center gap-4">
        <div className="h-16 w-16 rounded-full bg-white/15 border border-white/20 flex items-center justify-center font-display text-2xl font-bold">
          {(profile?.name || user?.name)?.[0]?.toUpperCase()}
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider text-white/70">Karyawan</div>
          <div className="font-display text-2xl font-bold">{profile?.name || user?.name}</div>
          <div className="text-sm text-white/80 mt-1">
            {profile ? `${profile.position} · ${profile.employee_id}` : user?.email}
          </div>
        </div>
        <div className="flex gap-2">
          {!todayAtt ? (
            <Button onClick={checkIn} disabled={checkingIn} className="bg-white text-[#0B8D95] hover:bg-white/90" data-testid="check-in-button">
              Check In
            </Button>
          ) : !todayAtt.check_out ? (
            <Button onClick={checkOut} disabled={checkingIn} className="bg-white text-[#0B8D95] hover:bg-white/90" data-testid="check-out-button">
              Check Out ({todayAtt.check_in})
            </Button>
          ) : (
            <div className="text-sm text-white/80 text-center">
              <div className="text-xs uppercase tracking-wider">Hari ini</div>
              <div className="font-mono">{todayAtt.check_in} - {todayAtt.check_out}</div>
            </div>
          )}
        </div>
      </div>

      <Tabs defaultValue="slip" className="w-full">
        <TabsList className="grid w-full grid-cols-4 bg-white border border-gray-200">
          <TabsTrigger value="slip" data-testid="portal-tab-slip"><Money size={16} className="mr-1.5" />Slip Gaji</TabsTrigger>
          <TabsTrigger value="cuti" data-testid="portal-tab-cuti"><CalendarBlank size={16} className="mr-1.5" />Cuti</TabsTrigger>
          <TabsTrigger value="rapor" data-testid="portal-tab-rapor"><IdentificationCard size={16} className="mr-1.5" />Rapor</TabsTrigger>
          <TabsTrigger value="absensi" data-testid="portal-tab-absensi"><ClockCounterClockwise size={16} className="mr-1.5" />Absensi</TabsTrigger>
        </TabsList>

        <TabsContent value="slip">
          <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                  <tr><th className="px-4 py-3 font-semibold">Periode</th><th className="px-4 py-3 font-semibold text-right">Bruto</th><th className="px-4 py-3 font-semibold text-right">Potongan</th><th className="px-4 py-3 font-semibold text-right">Net</th><th className="px-4 py-3 font-semibold text-right">Aksi</th></tr>
                </thead>
                <tbody>
                  {slips.map((s) => (
                    <tr key={s.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-mono">{s.period}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{formatIDR(s.gross)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-[#DC2626]">{formatIDR((s.potongan||0)+(s.pajak||0))}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-bold text-[#059669]">{formatIDR(s.net)}</td>
                      <td className="px-4 py-2.5 text-right"><button onClick={()=>printSlip(s)} className="p-1.5 hover:bg-gray-100 rounded" data-testid={`portal-slip-print-${s.id}`}><Printer size={14} /></button></td>
                    </tr>
                  ))}
                  {slips.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-gray-500">Belum ada slip gaji</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="cuti">
          <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex justify-between">
              <div className="text-sm text-gray-500">Riwayat pengajuan cuti Anda</div>
              <Button size="sm" className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" onClick={() => window.location.href = "/cuti"} data-testid="portal-request-cuti"><ArrowSquareOut size={14} className="mr-1" />Ajukan Baru</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                  <tr><th className="px-4 py-3 font-semibold">Jenis</th><th className="px-4 py-3 font-semibold">Tanggal</th><th className="px-4 py-3 font-semibold">Hari</th><th className="px-4 py-3 font-semibold">Alasan</th><th className="px-4 py-3 font-semibold">Status</th></tr>
                </thead>
                <tbody>
                  {leaves.map((l) => (
                    <tr key={l.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2.5">{l.leave_type}</td>
                      <td className="px-4 py-2.5 text-xs font-mono">{formatDate(l.start_date)} — {formatDate(l.end_date)}</td>
                      <td className="px-4 py-2.5 text-center">{l.days}</td>
                      <td className="px-4 py-2.5 truncate max-w-xs">{l.reason}</td>
                      <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded text-xs font-semibold ${l.status === "approved" ? "bg-[#CCFBF1] text-[#0B8D95]" : l.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{l.status}</span></td>
                    </tr>
                  ))}
                  {leaves.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-gray-500">Belum ada pengajuan</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="rapor">
          <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                  <tr><th className="px-4 py-3 font-semibold">Periode</th><th className="px-4 py-3 font-semibold text-center">Rata²</th><th className="px-4 py-3 font-semibold text-center">Grade</th><th className="px-4 py-3 font-semibold">Catatan</th></tr>
                </thead>
                <tbody>
                  {rapor.map((r) => (
                    <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2.5">{r.period}</td>
                      <td className="px-4 py-2.5 text-center font-mono font-bold">{r.average}</td>
                      <td className="px-4 py-2.5 text-center"><span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${r.grade==="A"?"bg-[#CCFBF1] text-[#0B8D95]":r.grade==="B"?"bg-blue-100 text-blue-700":r.grade==="C"?"bg-amber-100 text-amber-700":"bg-red-100 text-red-700"}`}>{r.grade}</span></td>
                      <td className="px-4 py-2.5 text-xs text-gray-600">{r.catatan || "-"}</td>
                    </tr>
                  ))}
                  {rapor.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-gray-500">Belum ada rapor</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="absensi">
          <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                  <tr><th className="px-4 py-3 font-semibold">Tanggal</th><th className="px-4 py-3 font-semibold">Check In</th><th className="px-4 py-3 font-semibold">Check Out</th><th className="px-4 py-3 font-semibold">Status</th></tr>
                </thead>
                <tbody>
                  {attendance.map((a) => (
                    <tr key={a.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-mono text-xs">{formatDate(a.date)}</td>
                      <td className="px-4 py-2.5 font-mono">{a.check_in || "-"}</td>
                      <td className="px-4 py-2.5 font-mono">{a.check_out || "-"}</td>
                      <td className="px-4 py-2.5 capitalize">{a.status}</td>
                    </tr>
                  ))}
                  {attendance.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-gray-500">Belum ada absensi</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
